import React from 'react';
import { Box, styled } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Button as APButton } from '../../../Buttons/Button';
import { SPACING, RADIUS, COLORS, TYPOGRAPHY, CALENDAR_WIDTH, CALENDAR_CELL_SIZE } from '../../constants';
import type { DateRangePickerButtonProps } from '../../types';
import { CalendarHeader } from '../CalendarHeader';
import { CustomDay } from '../CustomDay';
import { DateInputFields } from '../DateInputFields';

const PopoverContainer = styled(Box)({
  backgroundColor: COLORS.bgPrimary,
  borderRadius: `${RADIUS['2xl']}px`,
  overflow: 'hidden',
});

const CalendarsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const CalendarWrapper = styled(Box)(({ theme }) => ({
  width: `${CALENDAR_WIDTH}px`,
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    minWidth: `${CALENDAR_WIDTH}px`,
    maxWidth: '400px',
  },
}));

const LeftCalendarWrapper = styled(CalendarWrapper)(({ theme }) => ({
  borderRight: `1px solid ${COLORS.borderSecondary}`,
  [theme.breakpoints.down('sm')]: {
    borderRight: 'none',
    borderBottom: `1px solid ${COLORS.borderSecondary}`,
  },
}));

const RightCalendarWrapper = styled(CalendarWrapper)({});

const BottomPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: `${SPACING.lg}px`,
  padding: `${SPACING.xl}px`,
  borderTop: `1px solid ${COLORS.borderSecondary}`,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: `${SPACING.md}px`,
  },
}));

const ActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: `${SPACING.lg}px`,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    '& > *': {
      flex: 1,
    },
  },
}));

export type CalendarContainerProps = {
  leftMonth: Dayjs;
  rightMonth: Dayjs;
  tempStartDate: Dayjs | null;
  tempEndDate: Dayjs | null;
  hoveredDay: Dayjs | null;
  setHoveredDay: (day: Dayjs | null) => void;
  primaryColor: string;
  isMobile: boolean;
  locale: string;
  minDate?: string | null;
  maxDate?: string | null;
  onDateClick: (date: Dayjs) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  startInputValue: string;
  endInputValue: string;
  startInputError: boolean;
  endInputError: boolean;
  onStartInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEndInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStartInputBlur: () => void;
  onEndInputBlur: () => void;
  onStartInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onEndInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onApply: () => void;
  onCancel: () => void;
  t: DateRangePickerButtonProps['t'];
};

export const CalendarContainer: React.FC<CalendarContainerProps> = ({
  leftMonth,
  rightMonth,
  tempStartDate,
  tempEndDate,
  hoveredDay,
  setHoveredDay,
  primaryColor,
  isMobile,
  locale,
  minDate,
  maxDate,
  onDateClick,
  onPrevMonth,
  onNextMonth,
  startInputValue,
  endInputValue,
  startInputError,
  endInputError,
  onStartInputChange,
  onEndInputChange,
  onStartInputBlur,
  onEndInputBlur,
  onStartInputKeyDown,
  onEndInputKeyDown,
  onApply,
  onCancel,
  t,
}) => {
  // Create custom day slot with range highlighting
  const createDaySlot = React.useCallback(
    (props: {
      day: Dayjs | string | number;
      outsideCurrentMonth: boolean;
      isFirstVisibleCell: boolean;
      isLastVisibleCell: boolean;
      onDaySelect?: (day: Dayjs | string | number) => void;
    }) => {
      // Convert day to Dayjs (AdapterDayjs provides Dayjs)
      const dayDate = dayjs(props.day);
      const onDaySelect = props.onDaySelect ? (day: Dayjs) => props.onDaySelect?.(day) : undefined;
      return (
        <CustomDay
          day={dayDate}
          outsideCurrentMonth={props.outsideCurrentMonth}
          isFirstVisibleCell={props.isFirstVisibleCell}
          isLastVisibleCell={props.isLastVisibleCell}
          tempStartDate={tempStartDate}
          tempEndDate={tempEndDate}
          hoveredDay={hoveredDay}
          primaryColor={primaryColor}
          onDaySelect={onDaySelect}
          onMouseEnter={() => {
            if (tempStartDate && !tempEndDate) {
              setHoveredDay(dayDate);
            }
          }}
        />
      );
    },
    [tempStartDate, tempEndDate, hoveredDay, primaryColor, setHoveredDay],
  );

  // Calendar styling - hides the MUI calendar header since we render our own
  const calendarSx = {
    width: '100%',
    // Hide the built-in header completely since we render our own
    '& .MuiPickersCalendarHeader-root': {
      display: 'none',
    },
    '& .MuiDayCalendar-header': {
      paddingLeft: `${SPACING['3xl']}px`,
      paddingRight: `${SPACING['3xl']}px`,
    },
    '& .MuiDayCalendar-weekDayLabel': {
      ...TYPOGRAPHY.textSm,
      fontWeight: 500,
      color: COLORS.textSecondary,
      width: `${CALENDAR_CELL_SIZE}px`,
      height: `${CALENDAR_CELL_SIZE}px`,
    },
    '& .MuiDayCalendar-monthContainer': {
      paddingLeft: `${SPACING['3xl']}px`,
      paddingRight: `${SPACING['3xl']}px`,
      paddingBottom: `${SPACING['2xl']}px`,
    },
    '& .MuiPickersDay-root': {
      ...TYPOGRAPHY.textSm,
      fontWeight: 400,
      color: COLORS.textSecondary,
      width: `${CALENDAR_CELL_SIZE}px`,
      height: `${CALENDAR_CELL_SIZE}px`,
      margin: 0,
      '&.MuiPickersDay-today': {
        border: 'none',
      },
      '&.Mui-disabled': {
        color: COLORS.textDisabled,
      },
      '&:not(.Mui-selected):hover': {
        backgroundColor: COLORS.bgActive,
      },
    },
  };

  return (
    <PopoverContainer>
      <CalendarsContainer>
        {/* Left Calendar */}
        <LeftCalendarWrapper>
          <CalendarHeader
            month={leftMonth}
            locale={locale}
            onPrevMonth={onPrevMonth}
            onNextMonth={onNextMonth}
            showPrevButton={true}
            showNextButton={isMobile}
            isMobile={isMobile}
          />
          <DateCalendar
            key={`left-${leftMonth.format('YYYY-MM')}`}
            value={null}
            onChange={(date: Dayjs | null) => {
              if (date) {
                onDateClick(dayjs(date));
              }
            }}
            referenceDate={leftMonth}
            minDate={minDate ? dayjs(minDate) : undefined}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            slots={{ day: createDaySlot as never }}
            sx={calendarSx}
          />
        </LeftCalendarWrapper>

        {/* Right Calendar - hidden on mobile */}
        {!isMobile && (
          <RightCalendarWrapper>
            <CalendarHeader
              month={rightMonth}
              locale={locale}
              onPrevMonth={onPrevMonth}
              onNextMonth={onNextMonth}
              showPrevButton={false}
              showNextButton={true}
              isMobile={isMobile}
            />
            <DateCalendar
              key={`right-${rightMonth.format('YYYY-MM')}`}
              value={null}
              onChange={(date: Dayjs | null) => {
                if (date) {
                  onDateClick(dayjs(date));
                }
              }}
              referenceDate={rightMonth}
              minDate={minDate ? dayjs(minDate) : undefined}
              maxDate={maxDate ? dayjs(maxDate) : undefined}
              slots={{ day: createDaySlot as never }}
              sx={calendarSx}
            />
          </RightCalendarWrapper>
        )}
      </CalendarsContainer>

      <BottomPanel>
        <DateInputFields
          startInputValue={startInputValue}
          endInputValue={endInputValue}
          startInputError={startInputError}
          endInputError={endInputError}
          locale={locale}
          t={t}
          onStartInputChange={onStartInputChange}
          onEndInputChange={onEndInputChange}
          onStartInputBlur={onStartInputBlur}
          onEndInputBlur={onEndInputBlur}
          onStartInputKeyDown={onStartInputKeyDown}
          onEndInputKeyDown={onEndInputKeyDown}
        />
        <ActionsContainer>
          <APButton variant="contained" color="secondary" onClick={onCancel} size="md">
            {t('common:cancel')}
          </APButton>
          <APButton
            variant="contained"
            color="primary"
            onClick={onApply}
            size="md"
            disabled={startInputError || endInputError}
          >
            {t('common:confirm')}
          </APButton>
        </ActionsContainer>
      </BottomPanel>
    </PopoverContainer>
  );
};
